import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import NotificationCenter from '../../components/ui/NotificationCenter';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import SystemStatusIndicator from '../../components/ui/SystemStatusIndicator';
import CampaignTreeSidebar from './components/CampaignTreeSidebar';
import CampaignDataGrid from './components/CampaignDataGrid';
import CampaignAnalyticsPanel from './components/CampaignAnalyticsPanel';
import RealTimeEarningsWidget from './components/RealTimeEarningsWidget';
import QuickStatsCards from './components/QuickStatsCards';
import FilterBar from './components/FilterBar';

import Button from '../../components/ui/Button';

const PublisherDashboard = () => {
  const navigate = useNavigate();
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isSystemStatusOpen, setIsSystemStatusOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate initial data loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Keyboard shortcuts
    const handleKeyPress = (event) => {
      if (event?.ctrlKey || event?.metaKey) {
        switch (event?.key?.toLowerCase()) {
          case 'e':
            event?.preventDefault();
            handleQuickAction('export_data');
            break;
          case 'n':
            event?.preventDefault();
            navigate('/call-submission-interface');
            break;
          case 'r':
            event?.preventDefault();
            window.location?.reload();
            break;
          default:
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [navigate]);

  const handleCampaignSelect = (campaign) => {
    setSelectedCampaign(campaign);
  };

  const handleQuickAction = (actionId, items = []) => {
    console.log('Quick action:', actionId, 'Items:', items);
    
    switch (actionId) {
      case 'submit_bulk': navigate('/call-submission-interface');
        break;
      case 'export_data':
        // Handle export functionality
        break;
      case 'duplicate_campaign':
        // Handle campaign duplication
        break;
      case 'archive_items':
        // Handle archiving
        break;
      default:
        break;
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    console.log('Filters changed:', newFilters);
  };

  const handleNotificationToggle = () => {
    setIsNotificationOpen(!isNotificationOpen);
  };

  const handleSystemStatusToggle = () => {
    setIsSystemStatusOpen(!isSystemStatusOpen);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
          userRole="publisher" 
          isNotificationOpen={isNotificationOpen}
          onNotificationToggle={handleNotificationToggle}
        />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="text-lg font-semibold text-foreground mb-2">Loading Dashboard</h2>
            <p className="text-sm text-muted-foreground">Fetching your campaign data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header 
        userRole="publisher" 
        isNotificationOpen={isNotificationOpen}
        onNotificationToggle={handleNotificationToggle}
      />
      {/* Notification Center */}
      <NotificationCenter 
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
      />
      {/* Real-time Earnings Widget */}
      <RealTimeEarningsWidget />
      {/* Main Content */}
      <div className="pt-16">
        <div className="p-6">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Publisher Dashboard</h1>
              <p className="text-sm text-muted-foreground">
                Monitor campaign performance and optimize revenue across all traffic sources
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={() => window.location?.reload()}
              >
                Refresh
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => navigate('/call-submission-interface')}
              >
                Submit Calls
              </Button>
            </div>
          </div>

          {/* Quick Stats Cards */}
          <QuickStatsCards />

          {/* Filter Bar */}
          <FilterBar onFiltersChange={handleFiltersChange} />

          {/* Quick Action Toolbar */}
          <QuickActionToolbar
            section="publisher"
            selectedItems={selectedItems}
            onAction={handleQuickAction}
          />
        </div>

        {/* Three-Panel Layout */}
        <div className="flex h-[calc(100vh-400px)]">
          {/* Left Sidebar - Campaign Tree (25%) */}
          <div className="w-1/4 min-w-[300px]">
            <CampaignTreeSidebar
              onCampaignSelect={handleCampaignSelect}
              selectedCampaignId={selectedCampaign?.id}
            />
          </div>

          {/* Center Panel - Data Grid (50%) */}
          <div className="flex-1">
            <CampaignDataGrid
              selectedCampaign={selectedCampaign}
              onCampaignSelect={handleCampaignSelect}
            />
          </div>

          {/* Right Panel - Analytics (25%) */}
          <div className="w-1/4 min-w-[350px]">
            <CampaignAnalyticsPanel selectedCampaign={selectedCampaign} />
          </div>
        </div>
      </div>
      {/* System Status Indicator */}
      <SystemStatusIndicator
        isExpanded={isSystemStatusOpen}
        onToggle={handleSystemStatusToggle}
      />
      {/* Keyboard Shortcuts Help */}
      <div className="fixed bottom-4 left-4 z-1000">
        <div className="bg-card border border-border rounded-lg p-3 shadow-card">
          <div className="text-xs text-muted-foreground space-y-1">
            <div><kbd className="px-1 py-0.5 bg-muted rounded text-xs">Ctrl+N</kbd> New Call</div>
            <div><kbd className="px-1 py-0.5 bg-muted rounded text-xs">Ctrl+E</kbd> Export</div>
            <div><kbd className="px-1 py-0.5 bg-muted rounded text-xs">Ctrl+R</kbd> Refresh</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublisherDashboard;